// ============================================================================
// CINEMATIC CARD OVERLAY - Workflow outputs + device images as floating cards
// Cards fade in slowly, linger for 45s+, then fade out.
// Same overlap-avoidance and waiting queue pattern as MindBubbleOverlay.
// ============================================================================

import React, { useEffect, useState, useRef } from 'react';
import { useSecondScreenData } from '../../contexts/SecondScreenContext';
import type { CinematicCard } from '../../types/second-screen.types';

const FADE_IN_MS = 1500;
const FADE_OUT_MS = 2500;
const LINGER_MS = 45_000;          // how long card is fully visible before fade starts
const SAFETY_CLEAR_MS = 50_000;    // when component calls onDismiss (after fade completes)

// Card bounding box in % for overlap checking (generous — covers image cards)
const CARD_W = 30;
const CARD_H = 26;
const MARGIN = 3;

function randomPosition(): { left: number; top: number } {
  return {
    left: 2 + Math.random() * (78 - CARD_W),
    top:  2 + Math.random() * (96 - CARD_H),
  };
}

function overlaps(a: { left: number; top: number }, b: { left: number; top: number }): boolean {
  return (
    a.left < b.left + CARD_W + MARGIN &&
    a.left + CARD_W + MARGIN > b.left &&
    a.top  < b.top  + CARD_H + MARGIN &&
    a.top  + CARD_H + MARGIN > b.top
  );
}

function findFreePosition(occupied: { left: number; top: number }[]): { left: number; top: number } | null {
  for (let attempt = 0; attempt < 100; attempt++) {
    const pos = randomPosition();
    if (!occupied.some(o => overlaps(pos, o))) return pos;
  }
  return null;
}

// ============================================================================
// Single Card
// ============================================================================

interface CardProps {
  card: CinematicCard;
  position: { left: number; top: number };
  onDismiss: (id: string) => void;
}

const SingleCard: React.FC<CardProps> = ({ card, position, onDismiss }) => {
  const [opacity, setOpacity] = useState(0);
  const fadeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fade in
  useEffect(() => {
    const raf = requestAnimationFrame(() => setOpacity(1));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Schedule fade-out and dismiss
  useEffect(() => {
    fadeOutRef.current = setTimeout(() => setOpacity(0), LINGER_MS);
    dismissRef.current = setTimeout(() => onDismiss(card.id), SAFETY_CLEAR_MS);
    return () => {
      if (fadeOutRef.current) clearTimeout(fadeOutRef.current);
      if (dismissRef.current) clearTimeout(dismissRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = () => {
    setOpacity(0);
    setTimeout(() => onDismiss(card.id), FADE_OUT_MS);
  };

  const accent = card.accentColor || '#00c7c4';

  const glowStyle = {
    boxShadow: `0 0 20px 4px ${accent}44, 0 0 50px 10px ${accent}18`,
    borderColor: `${accent}55`,
  };

  return (
    <div
      className="absolute cursor-pointer"
      style={{
        left: `${position.left.toFixed(1)}%`,
        top: `${position.top.toFixed(1)}%`,
        width: '420px',
        maxWidth: '42vw',
        opacity,
        transition: `opacity ${opacity === 0 ? FADE_OUT_MS : FADE_IN_MS}ms ease`,
        pointerEvents: opacity > 0 ? 'auto' : 'none',
      }}
      onClick={handleClick}
    >
      <div
        className="rounded-2xl bg-black/75 backdrop-blur-md border overflow-hidden"
        style={glowStyle}
      >
        {/* Image card — thumbnail prominent */}
        {card.cardType === 'image' && card.imageUrl && (
          <div className="relative w-full aspect-video bg-black/40 overflow-hidden">
            <img
              src={card.imageUrl}
              alt={card.title}
              className="w-full h-full object-cover opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        )}

        {/* Content body */}
        <div className="px-5 py-4">
          {/* Event icon + type label for event cards */}
          {card.cardType === 'event' && (
            <div className="flex items-center gap-1.5 mb-2">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: accent }} />
              <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: accent }}>
                Event
              </span>
            </div>
          )}

          {/* Title */}
          <p className="text-white font-semibold text-sm leading-snug mb-1">{card.title}</p>

          {/* Subtitle */}
          {card.subtitle && (
            <p className="text-white/50 text-xs mb-2">{card.subtitle}</p>
          )}

          {/* Body — only for text/event cards or image captions */}
          {card.body && (
            <p className="text-white/75 text-xs leading-relaxed line-clamp-3">{card.body}</p>
          )}

          {/* Accent line */}
          <div className="mt-3 h-px w-8 rounded-full" style={{ backgroundColor: accent + '80' }} />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Overlay
// ============================================================================

const CinematicCardOverlay: React.FC = () => {
  const { activeCinematicCards, clearCinematicCard } = useSecondScreenData();
  const [, forceUpdate] = useState(0);

  const positionsRef = useRef<Map<string, { left: number; top: number }>>(new Map());
  const waitingRef = useRef<string[]>([]);

  const tryPlaceWaiting = (cardMap: Map<string, CinematicCard>) => {
    const stillWaiting: string[] = [];
    for (const id of waitingRef.current) {
      if (!cardMap.has(id)) continue;
      const occupied = Array.from(positionsRef.current.values());
      const pos = findFreePosition(occupied);
      if (pos !== null) {
        positionsRef.current.set(id, pos);
      } else {
        stillWaiting.push(id);
      }
    }
    waitingRef.current = stillWaiting;
  };

  const cardMap = new Map(activeCinematicCards.map(c => [c.id, c]));

  // Free positions for dismissed cards
  let anyFreed = false;
  for (const id of positionsRef.current.keys()) {
    if (!cardMap.has(id)) {
      positionsRef.current.delete(id);
      anyFreed = true;
    }
  }
  waitingRef.current = waitingRef.current.filter(id => cardMap.has(id));

  if (anyFreed && waitingRef.current.length > 0) {
    tryPlaceWaiting(cardMap);
  }

  // Assign positions to new cards
  for (const card of activeCinematicCards) {
    if (positionsRef.current.has(card.id)) continue;
    if (waitingRef.current.includes(card.id)) continue;
    const occupied = Array.from(positionsRef.current.values());
    const pos = findFreePosition(occupied);
    if (pos !== null) {
      positionsRef.current.set(card.id, pos);
    } else {
      waitingRef.current.push(card.id);
    }
  }

  const handleDismiss = (id: string) => {
    clearCinematicCard(id);
    positionsRef.current.delete(id);
    waitingRef.current = waitingRef.current.filter(i => i !== id);
    forceUpdate(n => n + 1);
  };

  const renderable = activeCinematicCards.filter(c => positionsRef.current.has(c.id));

  if (renderable.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[80] pointer-events-none">
      {renderable.map(card => (
        <div key={card.id} className="pointer-events-auto">
          <SingleCard
            card={card}
            position={positionsRef.current.get(card.id)!}
            onDismiss={handleDismiss}
          />
        </div>
      ))}
    </div>
  );
};

export default CinematicCardOverlay;
